import React, { useState, useMemo, useRef, useEffect } from 'react';
import './ConfigDiff.css';

interface ConfigDiffProps {
  original: any;
  modified: any;
  changeRequest?: string;
  changeSummary?: string;
  viewMode?: 'inline' | 'side-by-side';
}

// Helper function to calculate initial sections
const calculateInitialSections = (original: any, modified: any): Set<string> => {
  const sections = new Set<string>();
  const allKeys = new Set([
    ...Object.keys(original || {}),
    ...Object.keys(modified || {})
  ]);
  allKeys.forEach(key => sections.add(key));
  return sections;
};

const ConfigDiff: React.FC<ConfigDiffProps> = ({ 
  original, 
  modified, 
  changeRequest,
  changeSummary,
  viewMode: initialViewMode = 'inline'
}) => {
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    // Start with all sections expanded if there are 3 or fewer
    const sections = new Set<string>();
    const changes = calculateInitialSections(original, modified);
    if (changes.size <= 3) {
      changes.forEach(section => sections.add(section));
    }
    return sections;
  });
  
  // Refs for synchronized scrolling
  const leftColumnRef = useRef<HTMLDivElement>(null);
  const rightColumnRef = useRef<HTMLDivElement>(null);
  const isScrollingSyncRef = useRef(false);
  
  // Synchronized scrolling effect
  useEffect(() => {
    if (viewMode !== 'side-by-side') return;
    
    const handleScroll = (source: HTMLDivElement, target: HTMLDivElement) => {
      if (isScrollingSyncRef.current) return;
      
      isScrollingSyncRef.current = true;
      target.scrollTop = source.scrollTop;
      target.scrollLeft = source.scrollLeft;
      
      setTimeout(() => {
        isScrollingSyncRef.current = false;
      }, 10);
    };
    
    const leftScroll = () => {
      if (leftColumnRef.current && rightColumnRef.current) {
        handleScroll(leftColumnRef.current, rightColumnRef.current);
      }
    };
    
    const rightScroll = () => {
      if (leftColumnRef.current && rightColumnRef.current) {
        handleScroll(rightColumnRef.current, leftColumnRef.current);
      }
    };
    
    const leftCol = leftColumnRef.current;
    const rightCol = rightColumnRef.current;
    
    if (leftCol && rightCol) {
      leftCol.addEventListener('scroll', leftScroll);
      rightCol.addEventListener('scroll', rightScroll);
      
      return () => {
        leftCol.removeEventListener('scroll', leftScroll);
        rightCol.removeEventListener('scroll', rightScroll);
      };
    }
  }, [viewMode]);

  // Memoized JSON strings for performance
  const originalJson = useMemo(() => {
    try {
      return JSON.stringify(original, null, 2);
    } catch {
      return '[Error: Cannot stringify]';
    }
  }, [original]);
  
  const modifiedJson = useMemo(() => {
    try {
      return JSON.stringify(modified, null, 2);
    } catch {
      return '[Error: Cannot stringify]';
    }
  }, [modified]);

  // Deep diff calculation
  const calculateDiff = useMemo(() => {
    const diff: any = {};
    
    const deepDiff = (obj1: any, obj2: any, path: string = '') => {
      const allKeys = new Set([
        ...Object.keys(obj1 || {}),
        ...Object.keys(obj2 || {})
      ]);

      const changes: any[] = [];

      allKeys.forEach(key => {
        const currentPath = path ? `${path}.${key}` : key;
        const val1 = obj1?.[key];
        const val2 = obj2?.[key];

        if (val1 === undefined && val2 !== undefined) {
          // Added
          changes.push({
            path: currentPath,
            type: 'added',
            value: val2,
            oldValue: undefined
          });
        } else if (val1 !== undefined && val2 === undefined) {
          // Removed
          changes.push({
            path: currentPath,
            type: 'removed',
            value: undefined,
            oldValue: val1
          });
        } else if (
          typeof val1 === 'object' && 
          typeof val2 === 'object' && 
          val1 !== null && 
          val2 !== null && 
          !Array.isArray(val1) && 
          !Array.isArray(val2)
        ) {
          // Nested object - recurse (excluding null and arrays)
          const nestedChanges = deepDiff(val1, val2, currentPath);
          changes.push(...nestedChanges);
        } else if (Array.isArray(val1) && Array.isArray(val2)) {
          // Handle arrays specially
          if (JSON.stringify(val1) !== JSON.stringify(val2)) {
            changes.push({
              path: currentPath,
              type: 'modified',
              value: val2,
              oldValue: val1,
              isArray: true
            });
          }
        } else if (JSON.stringify(val1) !== JSON.stringify(val2)) {
          // Modified
          changes.push({
            path: currentPath,
            type: 'modified',
            value: val2,
            oldValue: val1
          });
        }
      });

      return changes;
    };

    return deepDiff(original, modified);
  }, [original, modified]);

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const renderValue = (value: any, changeType?: string): JSX.Element => {
    if (value === undefined) {
      return <span className="diff-undefined">undefined</span>;
    }
    
    if (value === null) {
      return <span className={changeType ? `diff-${changeType}` : ''}>null</span>;
    }
    
    const className = changeType ? `diff-${changeType}` : '';
    
    if (typeof value === 'string') {
      // Show whitespace clearly
      const displayValue = value
        .replace(/ /g, '·')
        .replace(/\t/g, '→')
        .replace(/\n/g, '↵\n');
      return <span className={className} title={value}>"{displayValue}"</span>;
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      return <span className={className}>{String(value)}</span>;
    } else if (Array.isArray(value)) {
      // Truncate large arrays
      const maxItems = 5;
      const items = value.slice(0, maxItems);
      const hasMore = value.length > maxItems;
      
      return (
        <span className={className}>
          [{items.map((item, i) => (
            <span key={i}>
              {typeof item === 'string' ? `"${item}"` : String(item)}
              {i < items.length - 1 && ', '}
            </span>
          ))}
          {hasMore && <span className="diff-truncated">... +{value.length - maxItems} more</span>}]
        </span>
      );
    } else if (typeof value === 'object' && value !== null) {
      // Truncate large objects
      const str = JSON.stringify(value, null, 2);
      if (str.length > 200) {
        return (
          <span className={className} title={str}>
            {str.substring(0, 200)}...
            <span className="diff-truncated"> (truncated)</span>
          </span>
        );
      }
      return <span className={className}>{str}</span>;
    }
    return <span className={className}>null</span>;
  };

  const renderInlineDiff = () => {
    // Group changes by top-level section
    const changesBySection: Record<string, any[]> = {};
    
    calculateDiff.forEach((change: any) => {
      const section = change.path.split('.')[0];
      if (!changesBySection[section]) {
        changesBySection[section] = [];
      }
      changesBySection[section].push(change);
    });

    return (
      <div className="diff-inline">
        {Object.entries(changesBySection).map(([section, changes]) => {
          const isExpanded = expandedSections.has(section);
          const hasChanges = changes.length > 0;
          
          return (
            <div key={section} className="diff-section">
              <div 
                className="diff-section-header"
                onClick={() => toggleSection(section)}
              >
                <span className="diff-section-toggle">
                  {isExpanded ? '▼' : '▶'}
                </span>
                <span className="diff-section-name">{section}</span>
                {hasChanges && (
                  <span className="diff-section-count">
                    {changes.length} change{changes.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              
              {isExpanded && (
                <div className="diff-section-content">
                  {changes.map((change: any, index: number) => (
                    <div key={index} className={`diff-item diff-item-${change.type}`}>
                      <div className="diff-path">{change.path}:</div>
                      <div className="diff-values">
                        {change.type === 'added' && (
                          <>
                            <span className="diff-label">Added: </span>
                            {renderValue(change.value, 'added')}
                          </>
                        )}
                        {change.type === 'removed' && (
                          <>
                            <span className="diff-label">Removed: </span>
                            {renderValue(change.oldValue, 'removed')}
                          </>
                        )}
                        {change.type === 'modified' && (
                          <>
                            <div className="diff-old">
                              <span className="diff-label">Was: </span>
                              {renderValue(change.oldValue, 'removed')}
                            </div>
                            <div className="diff-new">
                              <span className="diff-label">Now: </span>
                              {renderValue(change.value, 'added')}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderSideBySide = () => {
    // Use memoized JSON strings and split into lines
    const originalLines = originalJson.split('\n');
    const modifiedLines = modifiedJson.split('\n');
    
    // Improved diff algorithm using LCS (Longest Common Subsequence) approach
    const linePairs: Array<{
      original: string | null;
      modified: string | null;
      status: 'unchanged' | 'modified' | 'added' | 'removed';
      lineNum: number;
    }> = [];
    
    // Create maps for quick line lookup to detect moved lines
    const origLineMap = new Map<string, number[]>();
    originalLines.forEach((line, idx) => {
      const key = line.trim();
      if (!origLineMap.has(key)) {
        origLineMap.set(key, []);
      }
      origLineMap.get(key)!.push(idx);
    });
    
    const modLineMap = new Map<string, number[]>();
    modifiedLines.forEach((line, idx) => {
      const key = line.trim();
      if (!modLineMap.has(key)) {
        modLineMap.set(key, []);
      }
      modLineMap.get(key)!.push(idx);
    });
    
    // Build line mapping with improved diff detection
    const maxLines = Math.max(originalLines.length, modifiedLines.length);
    const usedOriginal = new Set<number>();
    const usedModified = new Set<number>();
    
    // First pass: exact matches
    for (let i = 0; i < Math.min(originalLines.length, modifiedLines.length); i++) {
      if (originalLines[i] === modifiedLines[i]) {
        linePairs.push({
          original: originalLines[i],
          modified: modifiedLines[i],
          status: 'unchanged',
          lineNum: i + 1
        });
        usedOriginal.add(i);
        usedModified.add(i);
      }
    }
    
    // Second pass: handle remaining lines
    for (let i = 0; i < maxLines; i++) {
      if (usedOriginal.has(i) || usedModified.has(i)) {
        continue; // Already processed
      }
      
      const origLine = i < originalLines.length ? originalLines[i] : null;
      const modLine = i < modifiedLines.length ? modifiedLines[i] : null;
      
      let status: 'unchanged' | 'modified' | 'added' | 'removed' = 'unchanged';
      
      if (origLine && modLine) {
        // Check if lines are similar (might be modified)
        const origTrimmed = origLine.trim();
        const modTrimmed = modLine.trim();
        
        // Calculate similarity
        const origCore = origTrimmed.replace(/[",:\s]/g, '');
        const modCore = modTrimmed.replace(/[",:\s]/g, '');
        
        if (origCore === modCore && origCore.length > 0) {
          status = 'unchanged'; // Just formatting difference
        } else if (origCore.length > 0 && modCore.length > 0) {
          // Check if it's actually a moved line
          const isOrigMoved = modLineMap.has(origTrimmed);
          const isModMoved = origLineMap.has(modTrimmed);
          
          if (isOrigMoved || isModMoved) {
            // One is moved, show as removed/added
            status = 'modified';
          } else {
            status = 'modified';
          }
        } else {
          status = 'modified';
        }
      } else if (!origLine && modLine) {
        status = 'added';
      } else if (origLine && !modLine) {
        status = 'removed';
      }
      
      linePairs.push({
        original: origLine,
        modified: modLine,
        status,
        lineNum: i + 1
      });
    }
    
    // Calculate change regions for better highlighting
    const changeRegions: Set<number> = new Set();
    linePairs.forEach((pair, idx) => {
      if (pair.status !== 'unchanged') {
        // Mark this line and surrounding context
        for (let j = Math.max(0, idx - 1); j <= Math.min(linePairs.length - 1, idx + 1); j++) {
          changeRegions.add(j);
        }
      }
    });
    
    return (
      <div className="diff-side-by-side">
        <div className="diff-column diff-column-original" ref={leftColumnRef}>
          <h4>Original</h4>
          <div className="diff-lines">
            {linePairs.map((pair, idx) => (
              <div
                key={idx}
                className={`diff-line ${
                  pair.status === 'removed' ? 'diff-line-removed' :
                  pair.status === 'modified' ? 'diff-line-modified' :
                  changeRegions.has(idx) ? 'diff-line-context' : ''
                }`}
              >
                <span className="diff-line-number">{pair.lineNum}</span>
                <pre className="diff-line-content">
                  {pair.original || ' '}
                </pre>
              </div>
            ))}
          </div>
        </div>
        <div className="diff-column diff-column-modified" ref={rightColumnRef}>
          <h4>Modified</h4>
          <div className="diff-lines">
            {linePairs.map((pair, idx) => (
              <div
                key={idx}
                className={`diff-line ${
                  pair.status === 'added' ? 'diff-line-added' :
                  pair.status === 'modified' ? 'diff-line-modified' :
                  changeRegions.has(idx) ? 'diff-line-context' : ''
                }`}
              >
                <span className="diff-line-number">{pair.lineNum}</span>
                <pre className="diff-line-content">
                  {pair.modified || ' '}
                </pre>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="config-diff">
      {(changeRequest || changeSummary) && (
        <div className="diff-header">
          {changeRequest && (
            <div className="diff-request">
              <strong>Change Request:</strong> {changeRequest}
            </div>
          )}
          {changeSummary && (
            <div className="diff-summary">
              <strong>Changes Made:</strong>
              <div className="diff-summary-content">
                {changeSummary.split('\n').map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="diff-controls">
        <button
          className={`diff-view-toggle ${viewMode === 'inline' ? 'active' : ''}`}
          onClick={() => setViewMode('inline')}
        >
          Inline Diff
        </button>
        <button
          className={`diff-view-toggle ${viewMode === 'side-by-side' ? 'active' : ''}`}
          onClick={() => setViewMode('side-by-side')}
        >
          Side by Side
        </button>
        {viewMode === 'inline' && (
          <>
            <button
              className="diff-view-toggle"
              onClick={() => {
                const allSections = calculateInitialSections(original, modified);
                setExpandedSections(allSections);
              }}
            >
              Expand All
            </button>
            <button
              className="diff-view-toggle"
              onClick={() => setExpandedSections(new Set())}
            >
              Collapse All
            </button>
          </>
        )}
        <span className="diff-stats">
          {calculateDiff.filter((c: any) => c.type === 'added').length} added,{' '}
          {calculateDiff.filter((c: any) => c.type === 'modified').length} modified,{' '}
          {calculateDiff.filter((c: any) => c.type === 'removed').length} removed
        </span>
      </div>

      <div className="diff-content">
        {viewMode === 'inline' ? renderInlineDiff() : renderSideBySide()}
      </div>
    </div>
  );
};

export default ConfigDiff;